import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * NotificationBell — sino flutuante que consome /api/notifications.
 * Poll a cada 30s. Dropdown com últimos 10 unread. Click em item marca como lido.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
const POLL_INTERVAL = 30000; // 30s

export default function NotificationBell({ variant = "inline" }) {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  // Fecha dropdown ao clicar fora.
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("nexo_token") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${BACKEND_URL}/api/notifications?limit=15`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      setItems(data.items || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) {
      // silencioso — backend pode estar offline
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const t = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => clearInterval(t);
  }, []);

  const markRead = async (id) => {
    try {
      const token = localStorage.getItem("nexo_token") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await fetch(`${BACKEND_URL}/api/notifications/${id}/read`, { method: "POST", headers });
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem("nexo_token") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await fetch(`${BACKEND_URL}/api/notifications/read-all`, { method: "POST", headers });
      setItems((prev) => prev.map((n) => ({ ...n, read_at: new Date().toISOString() })));
      setUnreadCount(0);
    } catch {}
  };

  const priorityColor = (p) => {
    if (p === "high") return "border-l-status-error";
    if (p === "low") return "border-l-foreground-muted";
    return "border-l-brand-cyan";
  };

  // FaseC C1: variant="inline" (novo default) renderiza o sino dentro do
  // header da seção, sem `fixed`. variant="floating" mantém o comportamento
  // legado (top-right com safe-area da FaseA) pra eventual fallback.
  const wrapperClass = variant === "floating"
    ? "fixed top-[calc(env(safe-area-inset-top,0px)+1rem)] right-[calc(env(safe-area-inset-right,0px)+1rem)] z-40"
    : "relative";
  return (
    <div ref={ref} className={wrapperClass}>
      <motion.button
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={cn(
          "relative w-11 h-11 rounded-full flex items-center justify-center transition-colors duration-normal ease-smooth",
          "bg-surface-card hover:bg-surface-card-hover border border-subtle",
          unreadCount > 0 && "border-brand-cyan/40"
        )}
        data-testid="notification-bell"
        aria-label="Notificações"
      >
        <Bell className={cn("w-5 h-5", unreadCount > 0 ? "text-brand-cyan" : "text-foreground-muted")} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-status-error text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            className="absolute top-14 right-0 w-96 max-h-[70vh] overflow-hidden rounded-lg bg-surface-raised border border-subtle shadow-xl flex flex-col"
            data-testid="notification-dropdown"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-subtle">
              <div className="flex items-center gap-2">
                <span className="font-display font-semibold text-foreground">Notificações</span>
                {unreadCount > 0 && (
                  <span className="text-caption text-brand-cyan">{unreadCount} novas</span>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-caption text-foreground-subtle hover:text-brand-cyan transition-colors"
                  data-testid="notification-mark-all-read"
                >
                  Marcar todas
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading && items.length === 0 && (
                <div className="p-4 text-center text-foreground-subtle text-caption">Carregando…</div>
              )}
              {!loading && items.length === 0 && (
                <div className="p-6 text-center text-foreground-subtle">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-caption">Sem notificações por aqui.</p>
                </div>
              )}
              {items.map((n) => (
                <div
                  key={n.id}
                  className={cn(
                    "px-4 py-3 border-l-2 border-b border-subtle hover:bg-surface-card-hover transition-colors duration-fast cursor-pointer",
                    priorityColor(n.priority),
                    !n.read_at && "bg-brand-cyan/5"
                  )}
                  onClick={() => !n.read_at && markRead(n.id)}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className={cn("text-sm text-foreground", !n.read_at && "font-medium")}>
                        {n.message}
                      </p>
                      <p className="text-micro text-foreground-subtle mt-1">
                        {formatTime(n.created_at)} · {typeLabel(n.type)}
                      </p>
                    </div>
                    {!n.read_at && (
                      <span className="w-2 h-2 rounded-full bg-brand-cyan mt-1.5 flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000; // em segundos
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString("pt-BR");
}

function typeLabel(type) {
  const labels = {
    reminder_due: "lembrete",
    briefing: "briefing",
    expense_anomaly: "alerta de gasto",
    habit_broken: "hábito",
    custom: "info",
  };
  return labels[type] || type;
}
